import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@base-ui/react/dialog';
import type { SiteMapAreaColor, SiteMapAreaIcon } from '@interfaces/ISiteMap';
import chrome from '../dialogChrome.module.css';
import { AREA_COLOR_TOKEN, AREA_ICONS } from './siteMap.constants';
import styles from './NewAreaDialog.module.css';

export interface INewAreaDialogProps {
  open: boolean;
  /** The create is in flight: Create stays disabled so a double-click cannot
   *  post the same area twice. */
  pending?: boolean;
  onCancel: () => void;
  onCreate: (v: { name: string; icon: SiteMapAreaIcon; color: SiteMapAreaColor }) => void;
}

const ICON_KEYS = Object.keys(AREA_ICONS) as SiteMapAreaIcon[];
const COLOR_KEYS = Object.keys(AREA_COLOR_TOKEN) as SiteMapAreaColor[];
const DEFAULT_ICON = ICON_KEYS[0]!;
const DEFAULT_COLOR = COLOR_KEYS[0]!;

/**
 * Draw-mode's final step: names the polygon just closed and assigns it a
 * fixed icon + colour (spec §5) before the `createArea` mutation fires.
 * Pure presentation — the caller (SiteMap.tsx, a later task) owns when the
 * dialog opens and what happens with the picked values.
 */
export function NewAreaDialog({ open, pending = false, onCancel, onCreate }: INewAreaDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<SiteMapAreaIcon>(DEFAULT_ICON);
  const [color, setColor] = useState<SiteMapAreaColor>(DEFAULT_COLOR);

  // Re-seed every time the dialog transitions to open — mirrors StockEntryDialog's
  // `wasOpen` idiom (adjusted during render, not an effect).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName('');
      setIcon(DEFAULT_ICON);
      setColor(DEFAULT_COLOR);
    }
  }

  const trimmedName = name.trim();
  const valid = trimmedName.length > 0;

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onCancel()}>
      <Dialog.Portal>
        <Dialog.Backdrop className={chrome.backdrop} />
        <Dialog.Popup className={`${chrome.popup} ${styles.popup}`}>
          <Dialog.Title className={chrome.title}>{t('logistics.siteMap.newArea.title')}</Dialog.Title>

          <div className={chrome.field}>
            <label className={chrome.label} htmlFor="new-area-name">
              {t('logistics.siteMap.newArea.name')}
            </label>
            <input
              id="new-area-name"
              type="text"
              className={chrome.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className={chrome.field}>
            <span className={chrome.label}>{t('logistics.siteMap.newArea.icon')}</span>
            <div
              className={styles.iconGrid}
              role="radiogroup"
              aria-label={t('logistics.siteMap.newArea.icon')}
            >
              {ICON_KEYS.map((key) => {
                const Icon = AREA_ICONS[key];
                const selected = icon === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={key}
                    className={selected ? `${styles.iconOption} ${styles.selected}` : styles.iconOption}
                    onClick={() => setIcon(key)}
                  >
                    <Icon size={18} />
                  </button>
                );
              })}
            </div>
          </div>

          <div className={chrome.field}>
            <span className={chrome.label}>{t('logistics.siteMap.newArea.color')}</span>
            <div
              className={styles.colorRow}
              role="radiogroup"
              aria-label={t('logistics.siteMap.newArea.color')}
            >
              {COLOR_KEYS.map((key) => {
                const selected = color === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={key}
                    className={selected ? `${styles.swatch} ${styles.selected}` : styles.swatch}
                    style={{ background: AREA_COLOR_TOKEN[key] }}
                    onClick={() => setColor(key)}
                  />
                );
              })}
            </div>
          </div>

          <div className={chrome.footer}>
            <button type="button" className={chrome.cancelBtn} onClick={onCancel}>
              {t('logistics.siteMap.newArea.cancel')}
            </button>
            <button
              type="button"
              className={chrome.saveBtn}
              disabled={!valid || pending}
              onClick={() => onCreate({ name: trimmedName, icon, color })}
            >
              {t('logistics.siteMap.newArea.create')}
            </button>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
